/**
 * Timebase Coupon Strategy
 * Handles TIMEBASE type coupons with time-based expiration
 */
import { BaseCouponStrategy } from './base-coupon-strategy';
import { couponTypes, couponDiscountTypeMap, couponSourceTypeMap } from '../helper/constant';
import { processCoupon, getDefaultCouponState } from '../helper/utils';
import _ from 'lodash';

export class TimebaseCouponStrategy extends BaseCouponStrategy {
  /**
   * Get coupon type identifier
   * @returns {string} TIMEBASE coupon type
   */
  getType() {
    return couponTypes.TIMEBASE;
  }

  /**
   * Calculate discount for TIMEBASE coupon
   * @param {Object} couponState - Current coupon state
   * @param {Array} selectedPlans - Selected plans array
   * @param {Object} planWithAmountMap - Plan amount mapping
   * @param {Object} context - Additional context
   * @returns {Object} Calculated discount result
   */
  // eslint-disable-next-line no-unused-vars
  calculateDiscount(couponState, selectedPlans, planWithAmountMap, context) {
    const discountPlanList = this.transformTimebaseToDiscountList(couponState);

    if (discountPlanList.length === 0) {
      return this.getInitialResult();
    }

    return processCoupon({
      discountPlanList,
      selectedPlanList: selectedPlans,
      planWithAmountMap,
    });
  }

  /**
   * Transform timebase coupon data to discount plan list
   * @param {Object} couponState - Coupon state with timeBase array
   * @returns {Array} List of discount plans
   */
  transformTimebaseToDiscountList(couponState) {
    const timeBase = couponState?.timeBase || [];

    return _.chain(timeBase)
      .reduce((accumulator, item) => {
        const { plan_code: planCode, price_mapping_plan_code, ...rest } = item;

        // Add main plan discount
        if (planCode) {
          accumulator.push({
            planCode,
            ...rest,
          });
        }

        // Add price mapping plan discount if exists (for price change cases)
        if (price_mapping_plan_code) {
          accumulator.push({
            planCode: price_mapping_plan_code,
            ...rest,
          });
        }

        return accumulator;
      }, [])
      .filter(Boolean)
      .value();
  }

  /**
   * Convert verify coupon API result to coupon state
   * @param {Object} apiResult - API response (array of plan discounts)
   * @param {Object} options - Conversion options
   * @returns {Object} Coupon state
   */
  convertApiResultToState(apiResult, options = {}) {
    const { couponCode = '', allowChange = true } = options;
    const defaultState = getDefaultCouponState();

    // Get coupon source type from first item with couponCategory
    const couponSourceType = _.find(apiResult, (v) => !!v.couponCategory)?.couponCategory;

    // Get coupon code from service if provided (for dynamic matched coupons)
    const couponCodeFromService = _.find(apiResult, (v) => !!v.couponCode)?.couponCode;

    // Transform API result to timeBase format
    const timeBase = _.chain(apiResult)
      .filter((item) => !!item.planCode)
      .map((v) => ({
        plan_code: v.planCode,
        category: v.couponCategory,
        discount: v.couponDiscount,
        discountType: v.couponDiscountType || couponDiscountTypeMap.percentage,
        expireDate: v.couponExpireDate,
        couponDefType: v?.couponDefType,
        couponPeriod: v?.couponPeriod,
      }))
      .value();

    // Get T&C message from first item with tncMessage
    const tncMessage = _.find(apiResult, (v) => !!v.tncMessage)?.tncMessage;

    return {
      ...defaultState,
      couponType: couponTypes.TIMEBASE,
      timeBase,
      tncMessage,
      couponCode: couponCodeFromService ?? couponCode,
      allowChange,
      couponSourceType,
      fromCampaignLink: couponSourceType === couponSourceTypeMap.marketing, // marketing campaign
    };
  }

  /**
   * Validate timebase coupon code
   * @param {string} couponCode - Coupon code to validate
   * @param {Object} context - Validation context
   * @returns {Promise<Object>} Validation result
   */
  // eslint-disable-next-line no-unused-vars
  async validate(couponCode, context) {
    // Timebase validation is typically done via verifyCoupon API
    // This can be extended for client-side validation if needed
    return {
      valid: true,
      couponCode,
    };
  }

  /**
   * Check if timebase coupon can be applied
   * @param {Object} context - Application context
   * @returns {boolean} Whether coupon can be applied
   */
  // eslint-disable-next-line no-unused-vars
  canApply(context) {
    // Timebase coupons can typically be applied in most flows
    return true;
  }
}

// Export singleton instance
export const timebaseCouponStrategy = new TimebaseCouponStrategy();
